import { useFormik } from "formik";
import * as Yup from "yup";
import Input from "../../../components/input";
import TextArea from "../../../components/textArea";
import Select from "../../../components/select";
import Button from "../../../components/button";
import { useDispatch, useSelector } from "react-redux";
import { createVolumeSnapshot } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PaymentModal from "../../../components/paymentModal";
import useZoneUnitPrices from "../../../hooks/useZoneUnitPrices";

const CreateVolumeSnapshot = () => {
  document.title = "Create Volume Snapshot - CloudVantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectId = useSelector((state) => state.user.selectedProject)?.id;
  const {getUnitPrice, loading: unitPriceLoading, } = useZoneUnitPrices(projectId);
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? Number(state.user.virtualAccount.dollar_balance) || 0
        : Number(state.user.virtualAccount.balance) || 0
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);
  const selectedVolume = JSON.parse(sessionStorage.getItem("selectedVolume"));

  const parsedVolumeSize = selectedVolume?.volumeSize
    ? typeof selectedVolume?.volumeSize === "number"
      ? selectedVolume?.volumeSize
      : parseFloat(selectedVolume?.volumeSize?.replace(/[^0-9.]/g, ""))
    : 0;

  const volumePaymentOptions = [
    { value: "", label: "Select an option" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

const calculateEstimatedCost = (
  volumeSize,
  currency,
  paymentMethod
) => {
  if (!volumeSize || !paymentMethod) {
    return {
      formatted: "0.00",
      numeric: 0,
    };
  }

  const snapshotPricePerGB = getUnitPrice(
    "volume_snapshot",
    currency
  );

  if (snapshotPricePerGB === null) {
    return {
      formatted: "Price unavailable",
      numeric: 0,
    };
  }

  const volumeSizeValue = parseFloat(volumeSize);

  let totalCost =
    volumeSizeValue * snapshotPricePerGB;

  if (paymentMethod === "yearly") {
    const yearlyPrice = totalCost * 12;
    const discount = yearlyPrice * 0.01;

    totalCost = yearlyPrice - discount;
  }

  return {
    formatted: totalCost.toLocaleString("en-US", {
      style: "currency",
      currency: currency === "NGN" ? "NGN" : "USD",
    }),
    numeric: totalCost,
  };
};

  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const createSnapshot = useFormik({
    initialValues: {
      snapshotName: "",
      payment: "",
      description: "",
    },
    validationSchema: Yup.object({
      snapshotName: Yup.string().required("Snapshot name cannot be empty"),
      payment: Yup.string().required("Payment method is required"),
      description: Yup.string().required("Description is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { snapshotName, payment, description } = values;

      let snapshotData = {
        name: snapshotName,
        volume_id: selectedVolume?.volumeId,
        payment_method: payment,
        description,
        projectId,
      };

      const { payload } = await dispatch(createVolumeSnapshot({ snapshotData, navigate }));
      if (payload.statuscode === "00") {
        navigate("/manageVolumeSnapshots");
        resetForm();
      }
    },
  });

  useEffect(() => {
    const estimate = calculateEstimatedCost(
      parsedVolumeSize,
      currency,
      createSnapshot.values.payment
    );
    setCalculatedEstimate(estimate.formatted);
  }, [parsedVolumeSize, currency, createSnapshot.values.payment]);

  const handleCreateSnapshotClick = (e) => {
    e.preventDefault();

    const estimate = calculateEstimatedCost(
      parsedVolumeSize,
      currency,
      createSnapshot.values.payment
    ).numeric;

    if (estimate > walletBalance) {
      setShowPaymentModal(true);
    } else {
      createSnapshot.handleSubmit();
    }
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue mb-6">
        Create Volume Snapshot for {selectedVolume?.volumeName}
      </p>

      <div className="grid gap-4">
        <form className="gap-y-6 grid">
          <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
            <Input
              type={"text"}
              label="Volume Snapshot Name"
              name={"snapshotName"}
              onBlur={createSnapshot.handleBlur}
              onChange={createSnapshot.handleChange}
              placeholder={"Give your snapshot a name"}
              value={createSnapshot.values.snapshotName}
              onError={
                createSnapshot.errors.snapshotName &&
                createSnapshot.touched.snapshotName
                  ? createSnapshot.errors.snapshotName
                  : null
              }
            />
            <Input
              type={"text"}
              label="Size"
              name={"size"}
              value={`${parsedVolumeSize} GB`}
              disabled={true}
            />
            <Select
              label="Payment:"
              name="payment"
              options={volumePaymentOptions}
              valueKey={"value"}
              labelKey={"label"}
              selectValue={createSnapshot.values.payment}
              selectBlur={createSnapshot.handleBlur}
              onChange={(event) => createSnapshot.setFieldValue("payment", event.target.value)}
              onError={
                createSnapshot.touched.payment && createSnapshot.errors.payment
                  ? createSnapshot.errors.payment
                  : null
              }
            />
            <TextArea
              label="Description"
              name="description"
              value={createSnapshot.values.description}
              onBlur={createSnapshot.handleBlur}
              onChange={createSnapshot.handleChange}
              onError={
                createSnapshot.errors.description &&
                createSnapshot.touched.description
                  ? createSnapshot.errors.description
                  : null
              }
            />
            <div className="flex justify-between items-center gap-2">
              <div>
                <div className="text-xs">Estimated cost:</div>
                <div className="font-semibold crimson-font text-primaryBlue text-lg">
                  {calculatedEstimate}
                </div>
              </div>
              <Button
            buttonRole="submit"
            disabled={unitPriceLoading}
            buttonText={
              unitPriceLoading
                ? "Loading price..."
                : calculateEstimatedCost(
                    parsedVolumeSize,
                    currency,
                    createSnapshot.values.payment
                  ).numeric > walletBalance
                  ? "Pay Now"
                  : "Create Volume Snapshot"
            }
            handleClick={(e) => handleCreateSnapshotClick(e)}
          />
            </div>
          </div>
        </form>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={calculateEstimatedCost(
          parsedVolumeSize,
          currency,
          createSnapshot.values.payment
        ).numeric}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => createSnapshot.handleSubmit()}
      />
    </div>
  );
};

export default CreateVolumeSnapshot;

